import { useState } from "react";
import { Star, ThumbsUp, ThumbsDown, PencilLine, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function Stars({ value, size = 4 }) {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => {
        const filled = i < full || (i === full && half);
        return (
          <Star
            key={i}
            className={`w-${size} h-${size} ${filled ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}
            strokeWidth={1.5}
          />
        );
      })}
    </div>
  );
}

function StarSelect({ value, onChange }) {
  return (
    <div className="flex items-center gap-1" data-testid="star-select">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          onClick={() => onChange(n)}
          data-testid={`star-select-${n}`}
          aria-label={`${n} stars`}
        >
          <Star className={`w-7 h-7 transition-colors ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-300"}`} strokeWidth={1.5} />
        </button>
      ))}
    </div>
  );
}

function ReviewForm({ storeSlug, storeName, onSubmitted, onCancel }) {
  const [author, setAuthor] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (comment.length < 10) return toast.error("Please write at least 10 characters");
    setLoading(true);
    try {
      await api.post(`/stores/${storeSlug}/reviews`, { author, rating, comment });
      toast.success("Thanks — review posted!");
      onSubmitted();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not post review");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onCancel} data-testid="review-form-modal">
      <div className="bg-white rounded-sm border border-slate-200 max-w-lg w-full p-8"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="overline mb-2">Share your experience</p>
            <h3 className="font-display text-2xl tracking-tight">Review {storeName}</h3>
          </div>
          <button onClick={onCancel} className="p-1.5 hover:bg-slate-100 rounded-sm" data-testid="review-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-5" data-testid="review-form">
          <div>
            <label className="overline text-slate-500 block mb-3">Your rating</label>
            <StarSelect value={rating} onChange={setRating} />
          </div>
          <div>
            <label className="overline text-slate-500 block mb-2">Your name</label>
            <input required maxLength={60} value={author}
                   onChange={(e) => setAuthor(e.target.value)}
                   placeholder="Jane D."
                   className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-slate-950 focus:outline-none"
                   data-testid="review-author-input" />
          </div>
          <div>
            <label className="overline text-slate-500 block mb-2">Your review</label>
            <textarea required minLength={10} maxLength={1200} rows={5} value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Did the coupons work? Was checkout smooth? Anything to warn other shoppers about?"
                      className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-slate-950 focus:outline-none resize-none"
                      data-testid="review-comment-input" />
            <p className="text-xs text-slate-400 mt-1">{comment.length}/1200</p>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onCancel}
                    className="flex-1 py-3 border border-slate-300 rounded-sm text-sm font-medium hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={loading} data-testid="review-submit-btn"
                    className="flex-1 py-3 bg-slate-950 hover:bg-emerald-500 hover:text-slate-950 text-white font-bold text-sm rounded-sm disabled:opacity-50">
              {loading ? "Posting…" : "Post review"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserReviewCard({ review }) {
  const [helpful, setHelpful] = useState(review.helpful || 0);
  const [unhelpful, setUnhelpful] = useState(review.unhelpful || 0);
  const [voted, setVoted] = useState(() =>
    typeof localStorage !== "undefined" && localStorage.getItem(`helpful_${review.id}`)
  );

  const vote = async (isHelpful) => {
    if (voted) return toast("You already voted");
    try {
      const { data } = await api.post(`/reviews/${review.id}/helpful`, { helpful: isHelpful });
      setHelpful(data.helpful);
      setUnhelpful(data.unhelpful);
      localStorage.setItem(`helpful_${review.id}`, isHelpful ? "up" : "down");
      setVoted(isHelpful ? "up" : "down");
    } catch {
      toast.error("Vote failed");
    }
  };

  return (
    <article className="border border-slate-200 p-6 rounded-sm bg-white" data-testid={`user-review-${review.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-display text-lg tracking-tight">{review.author}</p>
          <p className="text-xs text-slate-500">
            {new Date(review.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            <span className="ml-2 px-2 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-700 rounded-sm text-[10px] font-semibold tracking-wider uppercase">Shopper</span>
          </p>
        </div>
        <Stars value={review.rating} size={4} />
      </div>
      <p className="text-sm text-slate-700 leading-relaxed mb-4">&ldquo;{review.comment}&rdquo;</p>
      <div className="flex items-center gap-3 text-xs">
        <span className="text-slate-500">Was this helpful?</span>
        <button
          onClick={() => vote(true)}
          disabled={!!voted}
          data-testid={`helpful-up-${review.id}`}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm ${voted === "up" ? "bg-emerald-100 text-emerald-700" : "hover:bg-emerald-50 text-slate-500 disabled:opacity-50"}`}
        >
          <ThumbsUp className="w-3.5 h-3.5" /> {helpful}
        </button>
        <button
          onClick={() => vote(false)}
          disabled={!!voted}
          data-testid={`helpful-down-${review.id}`}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm ${voted === "down" ? "bg-red-100 text-red-700" : "hover:bg-red-50 text-slate-500 disabled:opacity-50"}`}
        >
          <ThumbsDown className="w-3.5 h-3.5" /> {unhelpful}
        </button>
      </div>
    </article>
  );
}

export default function StoreReviews({ reviews, storeName, storeSlug, onReviewPosted }) {
  const [showForm, setShowForm] = useState(false);
  if (!reviews) return null;
  const { average, count, distribution, recent, user_reviews = [] } = reviews;
  const maxDist = Math.max(...Object.values(distribution || { 5: 1 }));

  return (
    <section className="mt-20 border-t border-slate-200 pt-16" data-testid="store-reviews-section">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="overline mb-3">Community feedback</p>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">
            What shoppers say about {storeName}
          </h2>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="open-review-form-btn"
                className="inline-flex items-center gap-2 px-5 py-3 bg-slate-950 hover:bg-emerald-500 hover:text-slate-950 text-white font-bold text-sm rounded-sm transition-colors">
          <PencilLine className="w-4 h-4" /> Write a review
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-14">
        <div className="md:col-span-1 border border-slate-200 rounded-sm p-8 bg-slate-50" data-testid="rating-summary">
          <p className="font-display text-6xl tracking-tighter text-slate-950 mb-2" data-testid="rating-average">
            {average.toFixed(1)}
          </p>
          <Stars value={average} size={5} />
          <p className="text-sm text-slate-500 mt-3">
            Based on <span className="font-semibold text-slate-800">{count.toLocaleString()}</span> shopper reviews
          </p>
        </div>

        <div className="md:col-span-2 space-y-2">
          {[5, 4, 3, 2, 1].map((s) => {
            const c = distribution?.[s] ?? 0;
            const pct = maxDist > 0 ? (c / maxDist) * 100 : 0;
            return (
              <div key={s} className="flex items-center gap-4" data-testid={`rating-bar-${s}`}>
                <span className="text-sm font-medium text-slate-600 w-6">{s}★</span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full transition-all duration-500"
                       style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-slate-500 tabular-nums w-14 text-right">
                  {c.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {user_reviews.length > 0 && (
        <div className="mb-14">
          <h3 className="font-display text-xl tracking-tight mb-6">Shopper reviews ({user_reviews.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {user_reviews.map((r) => <UserReviewCard key={r.id} review={r} />)}
          </div>
        </div>
      )}

      <h3 className="font-display text-xl tracking-tight mb-6">Community insights</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {recent?.map((r) => (
          <article key={`${r.author}-${r.date}`} className="border border-slate-200 p-6 rounded-sm bg-white hover-lift"
                   data-testid={`review-${r.author.replace(/\s+/g,'-').toLowerCase()}`}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-display text-lg tracking-tight">{r.author}</p>
                <p className="text-xs text-slate-500">
                  {new Date(r.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </p>
              </div>
              <Stars value={r.rating} size={4} />
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">&ldquo;{r.comment}&rdquo;</p>
          </article>
        ))}
      </div>

      {showForm && (
        <ReviewForm
          storeSlug={storeSlug}
          storeName={storeName}
          onSubmitted={() => { setShowForm(false); onReviewPosted?.(); }}
          onCancel={() => setShowForm(false)}
        />
      )}
    </section>
  );
}
